import { ReactNode, useCallback, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Bond, Side } from '../data/dataProvider';
import { BidOfferSpread, RunBond } from '../data/runs';
import { Column, Sort, TableProps, TableSortIcon } from '../components/table/table';
import TablePercentileListBox from '../components/listbox/tablePercentileListBox';
import TableQuantityListBox, { quantityOptions } from '../components/listbox/tableQuantityListBox';
import { getPriceAsymptoticSpectrumBackground } from '../components/spectrum';
import { CusipCell } from '../components/data/cusipCell';
import { AlertCell } from '../components/data/alertCell';
import { AlertHeaderCell } from '../components/data/alertHeaderCell';
import { getLinkColumnConfig } from '../components/data/table.utils';
import { ColumnsOrderConfig, NO_RUN_BONDS_FOR_FILTERS, RunColumn } from './run.constants';
import { useUiMode } from '@hooks/useUiMode';
import { InferenceResult } from '@hooks/data/useSimpleInferenceData';
import { getColumnTitle, isPercentileSelectionDisabled } from './run.utils';
import { TableWithUserPreferences } from '../components/table/tableWithUserPreferences';
import { usePreviousMs } from '../components/filters/hooks/useFilters';
import { createNumberSort, createStringSort, sortByMaturity } from '@/utils/sort.utils';
import { TableBidOfferSpreadListBox } from '../components/listbox/tableBidOfferSpreadListBox';
import { PriceType } from '@/types/types';
import { FaCircleInfo } from 'react-icons/fa6'
import { Tooltip } from '@components/tooltip';
import { WarningTooltip } from '../components/warningTooltip';
import { RunMaxProbabilityResultPreview } from './components/runMaxProbabilityResultPreview';
import { Percentile } from '@/constants';
import { ExcelColumnConnection } from '@/excelApp/components/ExcelColumnConnection';
import { ExcelColumnSyncManager } from '@/excelApp/components/ExcelColumnSyncManager';
import { getColumnsValueFormatter } from './utils/getColumnsValueFormatter';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { selectHasCusipAccess } from '@/store/slices/auth.slice';
import { BidOfferSpreadType } from '@/types/enums';
import { TreasuryLink } from '../components/tresuryLink';

const tdCss = (selected: boolean) => clsx(selected ? 'bg-[#5D5F9D77]' : '', 'px-[0.3125rem] first:rounded-l-[0.625rem] last:rounded-r-[0.625rem] group-hover:bg-[#5D5F9D] group-hover:text-[#FBFBFD]');

const header = ({
  content, 
  sort, 
  background,
  infoText,
  columnId,
}:{ 
  content: ReactNode, 
  sort?: Sort, 
  background?: string, 
  infoText?: ReactNode,
  columnId?: RunColumn
}) => (
  <>
  <div className="border-[#5D5F9D] border-b-[0.0625rem] h-full px-[0.3125rem] w-full">
    <div className={clsx(background || '', 'flex flex-col h-full items-start justify-center px-[0.625rem] py-[0.625rem] rounded-t-[0.625rem] text-[0.875rem] text-[#C9CADE] text-start')}>
      <div className="gap-[0.4375rem] flex justify-center items-center">
        <div className='whitespace-nowrap font-bold'>{content}</div>
        {infoText && (
          <Tooltip tooltip={infoText} maxWidthCss='max-w-[20.625rem]' delayShow={350}>
            <FaCircleInfo onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        )}
        <TableSortIcon sort={sort} />
      </div>
      {/* component for excel add-in */}
      <ExcelColumnConnection type="run"  columnId={columnId} />
    </div>
  </div>

    
  </>
)
const cell = (contents: ReactNode, selected: boolean, background?: string) =>
  <div className={clsx(background && !selected ? `${background} group-hover:bg-[transparent]` : '', 'flex flex-col h-full justify-center px-[0.625rem] py-[1rem] text-[0.875rem]')}>
    {contents}
  </div>



const ONE_MILLION = 1000000;

const RunTable = ({
  inferenceResult,
  bonds,
  linkedFigis,
  remove,
  selectedBond,
  selectBond,
  setLinkedFigis,
  setRunBonds,
  readonly,
  tableRef,
}: {
  inferenceResult: InferenceResult<RunBond & Bond>;
  bonds: (RunBond & Bond)[],
  linkedFigis: Set<string>,
  remove: (figiSet: Set<string>) => void,
  selectedBond?: (RunBond & Bond) | null,
  selectBond: (bond: RunBond & Bond) => void,
  setLinkedFigis: React.Dispatch<React.SetStateAction<Set<string>>>
  setRunBonds: React.Dispatch<React.SetStateAction<(RunBond & Bond)[] | null | undefined>>
  readonly?: boolean;
  tableRef?: TableProps<RunBond & Bond, RunColumn>['tableRef'];
}) => {
  const { getFormattedPreviousDate } = usePreviousMs();
  const previousString = getFormattedPreviousDate();
  const gct = useCallback((c: RunColumn) => getColumnTitle(previousString)(c), [previousString]);
  const { isIgUiMode, isHyUiMode, uiMode } = useUiMode();
  const { data, createDataSort, createDataSortString } = inferenceResult;
  const hasCusipAccess = useSelector(selectHasCusipAccess);

  const createSelectValueHandler = useMemo(() =>
    (updateBond: (bond: Bond & RunBond) => Bond & RunBond) =>
      (b: Bond & RunBond) => {
        if (linkedFigis.has(b.figi)) {
          setRunBonds([
            ...bonds.filter(bond => !linkedFigis.has(bond.figi)),
            ...bonds.filter(bond => linkedFigis.has(bond.figi)).map(updateBond)
          ]);
        } else {
          setRunBonds([...bonds.filter(bond => bond.figi !== b.figi), updateBond(b)]);
        }
      }
    , [bonds, linkedFigis, setRunBonds]);

  const columns: Column<RunBond & Bond, RunColumn>[] = useMemo(() => {
    const columnsFormatter = getColumnsValueFormatter({ data, uiMode })


    const columns: Column<RunBond & Bond, RunColumn>[] = [
      {
        id: RunColumn.Alert,
        printable: false,
        draggable: false,
        Cell: (b, { selected }) => cell(<AlertCell cusip={b.cusip} />, selected),
        Header: () => header({
          content: <AlertHeaderCell />,
        }),
        tdCss,
      },
      {
        id: RunColumn.Cusip,
        getValue: columnsFormatter[RunColumn.Cusip]?.getValue,
        Cell: (b, { selected, value }) => cell(<CusipCell cusip={value as string} />, selected),
        Header: sort => header({
          content: gct(RunColumn.Cusip), 
          sort,
          columnId: hasCusipAccess ? RunColumn.Cusip : undefined,
        }),
        sort: (a, b) => a['cusip'] > b['cusip'] ? 1 : a['cusip'] < b['cusip'] ? -1 : 0,
        tdCss,
      },
      {
        id: RunColumn.TreasuryCusip,
        getValue: columnsFormatter[RunColumn.TreasuryCusip]?.getValue,
        Cell: (b, { selected, value }) => cell(<TreasuryLink cusip={value as string} />, selected),
        Header: sort => header({
          content: gct(RunColumn.TreasuryCusip),
          sort,
          columnId: RunColumn.TreasuryCusip,
        }),
        sort: createDataSortString(Side.bid, 'spread', 'treasury_cusip'),
        tdCss,
        onCellClick: (e) => {
          e.stopPropagation();
        }
      },
      {
        id: RunColumn.Ticker,
        getValue: columnsFormatter[RunColumn.Ticker]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected),
        Header: sort => header({
          content: gct(RunColumn.Ticker), 
          sort,
          columnId: RunColumn.Ticker,
        }),
        sort: (a, b) => a['ticker'] > b['ticker'] ? 1 : a['ticker'] < b['ticker'] ? -1 : 0,
        tdCss,
      },
      {
        id: RunColumn.Coupon,
        getValue: columnsFormatter[RunColumn.Coupon]?.getValue,
        Cell: (b, { selected, value }) => cell(`${value}%`, selected),
        Header: sort => header({
          content: gct(RunColumn.Coupon), 
          sort,
          columnId: RunColumn.Coupon,
        }),
        sort: (a, b) => a['coupon'] - b['coupon'],
        tdCss,
      },
      {
        id: RunColumn.Maturity,
        getValue: columnsFormatter[RunColumn.Maturity]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected),
        Header: sort => header({ 
          content: gct(RunColumn.Maturity), 
          sort,
          columnId: RunColumn.Maturity,
        }),
        sort: sortByMaturity,
        tdCss
      },
      {
        id: RunColumn.SAndPRating,
        getValue: columnsFormatter[RunColumn.SAndPRating]?.getValue,
        Cell: (b, { selected }) => cell(b['rating'] || '-', selected),
        Header: sort => header({
          content: gct(RunColumn.SAndPRating), 
          sort,
        }),
        sort: createStringSort('rating'),
        tdCss
      },
      {
        id: RunColumn.Series,
        getValue: columnsFormatter[RunColumn.Series]?.getValue,
        Cell: (b, { selected, value }) => cell(value || '-', selected),
        Header: sort => header({ 
          content: gct(RunColumn.Series), 
          sort,
          columnId: RunColumn.Series
        }),
        sort: (a, b) => a['series'] > b['series'] ? 1 : a['series'] < b['series'] ? -1 : 0,
        tdCss
      },
      {
        id: RunColumn.Tenor,
        getValue: columnsFormatter[RunColumn.Tenor]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected),
        Header: sort => header({
          content: gct(RunColumn.Tenor), 
          sort,
          columnId: RunColumn.Tenor
        }),
        sort: isIgUiMode 
          ? createDataSort(Side.bid, 'spread', 'tenor')
          : createDataSort(Side.bid, 'price', 'tenor'),
        tdCss
      },
      {
        id: RunColumn.AmountOutstanding,
        getValue: columnsFormatter[RunColumn.AmountOutstanding]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected),
        Header: sort => header({ 
          content: gct(RunColumn.AmountOutstanding), 
          sort,
          columnId: RunColumn.AmountOutstanding,
        }),
        sort: createNumberSort('amountOutstanding'),
        tdCss
      },
      {
        id: RunColumn.BOSpd,
        getValue: columnsFormatter[RunColumn.BOSpd]?.getValue,
        getPrintValue: columnsFormatter[RunColumn.BOSpd]?.getPrintValue,
        Cell: (b, { selected, value }) => {
          const v  = value as any as BidOfferSpread;

          const type = v.type === BidOfferSpreadType.Profit || v.type === BidOfferSpreadType.Range
            ? 'profit' as const
            : v.type === BidOfferSpreadType.Fixed
              ? 'probability' as const
              : 'quantiles' as const

          const priceType = isIgUiMode ? PriceType.Spread : PriceType.Price;
          const error = data[b.figi][Side.bid][priceType].data?.error;
          const calcData = data[b.figi][Side.bid][priceType].data?.data;

          return cell(
            <div className='flex items-center gap-2'>
              <TableBidOfferSpreadListBox
                value={v}
                disabled={readonly}
                selectValue={v => {
                  createSelectValueHandler(bond => {
                    return {
                      ...bond,
                      bidOfferSpreadV2: {
                        ...bond.bidOfferSpreadV2,
                        [uiMode]: v,
                      }
                    }
                  })(b)
                }}
              />
              {error && (
                <WarningTooltip id={`bospread-error-${b.figi}`} className='text-[#FB275a] w-5'>
                  {error}
                </WarningTooltip>
              )}
              {!error && (
                <RunMaxProbabilityResultPreview 
                  data={calcData as any}
                  type={type} 
                  inferenceData={data[b.figi]}
                />
              )}
              
            </div>
            , selected
          )
        },
        Header: sort => header({
          content: gct(RunColumn.BOSpd),
          sort,
          infoText: <>
            <p className='mb-4 text-[#C9CADE]'>Select how the bid/offer spread is generated.</p>

            <p className='mb-4'>
              <strong className='font-medium'>Percentile:</strong>
              {' '}
              <span className='text-[#C9CADE]'>User selects the bid and offer percentiles. Those percentiles are rounded, if rounding is enabled, to form the bid and offer prices displayed</span>
            </p>

            <p className='mb-4'>
              <strong className='font-medium'>Numeric Values:</strong>
              {' '}
              <span className='text-[#C9CADE]'>If you select one of the numeric values, you are fixing the Bid / Offer Spread, i.e., the difference between the bid and offer. Then, the bid and offers are chosen to maximize the joint probability of crossing a buy and sell trade at the bid and offer.</span>
            </p>

            <p className='mb-4'>
              <strong className='font-medium'>Profit:</strong>
              {' '}
              <span className='text-[#C9CADE]'>The bid and offer are automatically chosen to maximize the expected profit. The expected profit is defined as (Bid / Offer Spread) * (Trade Joint Probability), where the Trade Joint Probability = (1 - bid percentile) * (1 - offer percentile). Disclaimer: the bid and offer percentiles are generated by our machine learning model and don't necessarily reflect the true probability of trading. Therefore the expected profit is not necessarily reflective of the true profit or the true probability of a profit. </span>
            </p>
          </>
        }),
        sort: (a, b) => {
          const aSpread = a.bidOfferSpreadV2[uiMode];
          const bSpread = b.bidOfferSpreadV2[uiMode];

          if (aSpread.type === BidOfferSpreadType.Fixed && bSpread.type === BidOfferSpreadType.Fixed) {
            return aSpread.value - bSpread.value;
          }

          if (aSpread.type === BidOfferSpreadType.Fixed) {
            return 1;
          }

          if (bSpread.type === BidOfferSpreadType.Fixed) {
            return -1;
          }

          if (aSpread.type === BidOfferSpreadType.Range && bSpread.type === BidOfferSpreadType.Range) {
            return aSpread.min - bSpread.min || aSpread.max - bSpread.max;
          }

          if (aSpread.type === BidOfferSpreadType.Range) {
            return 1;
          }

          if (bSpread.type === BidOfferSpreadType.Range) {
            return -1;
          }

          return 0;
        },
        onCellClick: e => void e.stopPropagation(),
        tdCss
      },
      {
        id: RunColumn.BidPercentile,
        getValue: columnsFormatter[RunColumn.BidPercentile]?.getValue,
        Cell: (b, { selected, value }) => {
          const boSpread = b.bidOfferSpreadV2[uiMode];
          const disabled = readonly || isPercentileSelectionDisabled(boSpread)
          const disabledReason = readonly 
            ? 'Editing is not allowed'
            : `Change the "${gct(RunColumn.BOSpd)}" column value to "Percentile" to be able to set custom bid/offer percentiles`

          return cell(
            <TablePercentileListBox
              value={`${value}` as Percentile}
              selectValue={v => void createSelectValueHandler(bond => ({ ...bond, bidPercentile: +v }))(b)}
              disabled={disabled}
              disabledTooltipText={disabledReason}
            />
            ,
            selected
          )
        },
        Header: sort => header({
          content: gct(RunColumn.BidPercentile), 
          sort,
          columnId: RunColumn.BidPercentile,
        }),
        sort: (a, b) => a.bidPercentile - b.bidPercentile,
        onCellClick: e => void e.stopPropagation(),
        tdCss
      },
      {
        id: RunColumn.OfferPercentile,
        getValue: columnsFormatter[RunColumn.OfferPercentile]?.getValue,
        Cell: (b, { selected, value }) => {
          const boSpread = b.bidOfferSpreadV2[uiMode];
          const disabled = readonly || isPercentileSelectionDisabled(boSpread)
          const disabledReason = readonly
            ? 'Editing is not allowed'
            : `Change the "${gct(RunColumn.BOSpd)}" column value to "Percentile" to be able to set custom bid/offer percentiles`;

          return (
            cell(
              <TablePercentileListBox
                value={`${value}` as Percentile}
                selectValue={v => void createSelectValueHandler(bond => ({ ...bond, offerPercentile: +v }))(b)}
                disabled={disabled}
                disabledTooltipText={disabledReason}
              />
              , selected)
          )
        },
        Header: sort => header({
          content: gct(RunColumn.OfferPercentile), 
          sort,
          columnId: RunColumn.OfferPercentile,
        }),
        sort: (a, b) => a.offerPercentile - b.offerPercentile,
        onCellClick: e => void e.stopPropagation(),
        tdCss
      },
      {
        id: RunColumn.BidSpd,
        getPrintValue: columnsFormatter[RunColumn.BidSpd]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidSpd]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, isIgUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'),
        Header: sort => header({ 
          content: gct(RunColumn.BidSpd), 
          sort, 
          columnId: RunColumn.BidSpd,
          background: isIgUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'spread', 'current'),
        tdCss,
        printColumns: [
          {
            id: RunColumn.BidSpdTime,
            getValue: columnsFormatter[RunColumn.BidSpdTime]?.getValue!,
          }
        ],
      },
      {
        id: RunColumn.OfferSpd,
        getPrintValue: columnsFormatter[RunColumn.OfferSpd]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.OfferSpd]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, isIgUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.OfferSpd), 
          sort, 
          columnId: RunColumn.OfferSpd,
          background: isIgUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.offer, 'spread', 'current'),
        tdCss,
      },

      {
        id: RunColumn.BYTM,
        getPrintValue: columnsFormatter[RunColumn.BYTM]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BYTM]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.BYTM), 
          sort, 
          columnId: RunColumn.BYTM,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'ytm', 'current'),
        tdCss,
      },
      {
        id: RunColumn.OfferYTM,
        getPrintValue: columnsFormatter[RunColumn.OfferYTM]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.OfferYTM]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, 'bg-[#484A7A]'),
        Header: sort => header({ 
          content: gct(RunColumn.OfferYTM), 
          sort, 
          columnId: RunColumn.OfferYTM,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.offer, 'ytm', 'current'),
        tdCss,
      },

      {
        id: RunColumn.BidPx,
        getPrintValue: columnsFormatter[RunColumn.BidPx]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidPx]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, isHyUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.BidPx), 
          sort, 
          columnId: RunColumn.BidPx,
          background: isHyUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'price', 'current'),
        tdCss,
        printColumns: [
          {
            id: RunColumn.BidPxTime,
            getValue: (b) => data[b.figi][Side.offer].price.currentTime,
          }
        ],
      },
      {
        id: RunColumn.OfferPx,
        getPrintValue: columnsFormatter[RunColumn.OfferPx]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.OfferPx]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, isHyUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.OfferPx), 
          sort, 
          columnId: RunColumn.OfferPx,
          background: isHyUiMode ? 'bg-[#0A0B11]' : 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.offer, 'price', 'current'),
        tdCss,
      },

      {
        id: RunColumn.BidSize,
        getPrintValue: columnsFormatter[RunColumn.BidSize]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidSize]?.getValue,
        Cell: (b, { selected, value }) => {
          return cell(
            <TableQuantityListBox
              disabled={readonly}
              value={value as (typeof quantityOptions)[number]}
              selectValue={v => void createSelectValueHandler(bond => ({ ...bond, bidSize: +v * ONE_MILLION }))(b)}
            />, 
            selected, 
            'bg-[#484A7A]'
          )
        },
        Header: sort => header({
          content: gct(RunColumn.BidSize), 
          sort, 
          columnId: RunColumn.BidSize,
          background: 'bg-[#484A7A]'
        }),
        onCellClick: e => e.stopPropagation(),
        sort: (a, b) => a.offerSize - b.offerSize,
        tdCss
      },
      {
        id: RunColumn.OfferSize,
        getPrintValue: columnsFormatter[RunColumn.OfferSize]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.OfferSize]?.getValue,
        Cell: (b, { selected, value }) =>
          cell(
            <TableQuantityListBox
              disabled={readonly}
              value={value as (typeof quantityOptions)[number]}
              selectValue={v => void createSelectValueHandler(bond => ({ ...bond, offerSize: +v * ONE_MILLION }))(b)}
            />
            , selected, 'bg-[#484A7A]'),
        Header: sort => header({ 
          content: gct(RunColumn.OfferSize), 
          sort, 
          columnId: RunColumn.OfferSize,
          background: 'bg-[#484A7A]'
        }),
        onCellClick: e => e.stopPropagation(),
        sort: (a, b) => a.offerSize - b.offerSize,
        tdCss
      },
      {
        id: RunColumn.BidSpdChange,
        getPrintValue: columnsFormatter[RunColumn.BidSpdChange]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidSpdChange]?.getValue,
        Cell: (b, { selected, value }) =>
          cell(
            value,
            selected,
            getPriceAsymptoticSpectrumBackground(data[b.figi][Side.bid].spread.diff, 'light', 'spread')
          ),
        Header: sort => header({ 
          content: gct(RunColumn.BidSpdChange), 
          sort, 
          columnId: RunColumn.BidSpdChange,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'spread', 'diff'),
        tdCss
      },
      {
        id: RunColumn.BYTMChange,
        getPrintValue: columnsFormatter[RunColumn.BYTMChange]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BYTMChange]?.getValue,
        Cell: (b, { selected, value }) =>
          cell(
            value,
            selected,
            getPriceAsymptoticSpectrumBackground(data[b.figi][Side.bid].ytm.diff, 'light', 'ytm')
          ),
        Header: sort => header({
          content: gct(RunColumn.BYTMChange), 
          sort, 
          columnId: RunColumn.BYTMChange,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'ytm', 'diff'),
        tdCss
      },
      {
        id: RunColumn.BidPxChange,
        getPrintValue: columnsFormatter[RunColumn.BidPxChange]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidPxChange]?.getValue,
        Cell: (b, { selected, value }) =>
          cell(
            value,
            selected,
            getPriceAsymptoticSpectrumBackground(data[b.figi][Side.bid].price.diff, 'light', 'price')
          ),
        Header: sort => header({
          content: gct(RunColumn.BidPxChange), 
          sort, 
          columnId: RunColumn.BidPxChange,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'price', 'diff'),
        tdCss
      },
      {
        id: RunColumn.BidSpdPrev,
        getPrintValue: columnsFormatter[RunColumn.BidSpdPrev]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidSpdPrev]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.BidSpdPrev), 
          sort, 
          columnId: RunColumn.BidSpdPrev,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'spread', 'prev'),
        tdCss,
        printColumns: [
          {
            id: RunColumn.BidSpdPrevTime,
            getValue: (b) => data[b.figi][Side.bid].spread.prevTime,
          }
        ],
      },
      {
        id: RunColumn.BYTMPrev,
        getPrintValue: columnsFormatter[RunColumn.BYTMPrev]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BYTMPrev]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.BYTMPrev), 
          sort, 
          columnId: RunColumn.BYTMPrev,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'ytm', 'prev'),
        tdCss,
        printColumns: [
          {
            id: RunColumn.BYTMPrevTime,
            getValue: (b) => data[b.figi][Side.bid].ytm.prevTime,
          }
        ],
      },
      {
        id: RunColumn.BidPxPrev,
        getPrintValue: columnsFormatter[RunColumn.BidPxPrev]?.getPrintValue,
        getValue: columnsFormatter[RunColumn.BidPxPrev]?.getValue,
        Cell: (b, { selected, value }) => cell(value, selected, 'bg-[#484A7A]'),
        Header: sort => header({
          content: gct(RunColumn.BidPxPrev), 
          sort, 
          columnId: RunColumn.BidPxPrev,
          background: 'bg-[#484A7A]'
        }),
        sort: createDataSort(Side.bid, 'price', 'prev'),
        tdCss,
        printColumns: [
          {
            id: RunColumn.BidPxPrevTime,
            getValue: (b) => data[b.figi][Side.bid].price.prevTime,
          }
        ],
      },
    ];

    if (!readonly) {
      // link column
      columns.unshift({
        tdCss,
        ...getLinkColumnConfig({
          cell,
          header: (content) => header({ content }),
          items: bonds,
          selectedItems: linkedFigis,
          setSelectedItems: setLinkedFigis
        }),
        id: RunColumn.Link,
      },)

      // delete column
      columns.push({
        id: RunColumn.Remove,
        printable: false,
        draggable: false,
        Cell: (b, { selected }) => cell(
          <button
            className="bg-transparent h-[1.5rem] flex flex-col items-center justify-center rounded-full w-[1.5rem]"
          // onClick handled for the entire cell
          >
            <XMarkIcon className="h-[1.25rem] text-[#4384C8] w-[1.25rem]" />
          </button>
          , selected),
        Header: sort => header({
          content: gct(RunColumn.Remove), 
          sort
        }),
        onCellClick: (e, bond) => {
          e.stopPropagation();
          if (linkedFigis.has(bond.figi)) {
            remove(linkedFigis);
          } else {
            remove(new Set([bond.figi]));
          }
        },
        tdCss
      })
    }

    return columns;
  }, [bonds, createDataSort, createSelectValueHandler, data, remove, linkedFigis, setLinkedFigis, gct, isIgUiMode, isHyUiMode, uiMode, readonly, hasCusipAccess]);

  const trCss = useMemo(() =>
    (b: Bond & RunBond) =>
      clsx(
        linkedFigis.has(b.figi)
          ? 'bg-gradient-to-r from-[#5D5F9D77]'
          : '',
        'rounded-[0.625rem] hover:shadow-[-0.375rem_-0.375rem_1.875rem_0_#615EFF66,0.625rem_0.625rem_1.875rem_0_#07011F59]'
      )
    , [linkedFigis])



  return (
    <>
    <TableWithUserPreferences
      columns={columns}
      generateItemKey={b => b.cusip}
      initialSortColumn={RunColumn.Maturity}
      initialSortDirection='ascending'
      items={bonds}
      onRowClick={(_, bond) => void selectBond(bond)}
      selectedItem={selectedBond}
      tableCss='mx-[0.625rem]'
      tableName='r'
      theadCss='bg-[#333557] sticky top-0 z-[2]'
      trCss={trCss}
      sortPriorityClassName='mx-2.5'
      getColumnFilterLabel={gct}
      tableRef={tableRef}
      columnsOrderConfig={ColumnsOrderConfig}
      preferenceTableName='run'
      noDataMessage={NO_RUN_BONDS_FOR_FILTERS}
      useSearchPagination
      // defaultPageSize={25}
    />
    
      {/* Sync with excel works only in excel add-in */}
      <ExcelColumnSyncManager tableRef={tableRef} items={bonds} data={data} />
    </>
  );
}

export default RunTable;
